import { AgGridReactProps } from '@ag-grid-community/react';
import { Box, Grid } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { ClientWalletRolesMap } from '@tassat/common-definitions';
import { UserAccountMapped } from '@tassat/fred-extended-definitions';
import {
    Favorite as FavoriteIcon,
    Lock as LockIcon
} from '@tassat/icon';
import { AttributeBadge, AttributeTag, ClientRoleSymbol } from '@tassat/symbols';
import React from 'react';
import { LinkProps, Link as RouterLink } from 'react-router-dom';

import { LOCK_ICON_COLOR } from 'Lib/constant';
import { obscureAccountNo } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

const Link = (RouterLink as unknown) as React.ComponentClass<LinkProps>;

interface IWalletListItemProps extends AgGridReactProps {
    data: UserAccountMapped;
}

const muiStyles = makeStyles((theme: ApplicationMuiTheme) => {
    const activeBorder = theme?.app?.clientTheme.colors.sidelist?.activeBorder ||
        theme?.app?.applicationColors.success ||
        '#A5D673';

    const disabledBorder = theme?.app?.clientTheme.colors.sidelist?.disabledBorder ||
        theme?.app?.applicationColors.failureLight ||
        '#FF9B6B';

    return {
        root: {
            minHeight: '60px',
            maxHeight: '122px',
            height: '100%',
            color: 'inherit',
            textDecoration: 'inherit',
            borderLeft: (props: IWalletListItemProps) => `5px solid ${!props.data.account.IsLocked ?
                activeBorder
                : disabledBorder}`,
            paddingLeft: '1rem',
            backgroundColor: (props: IWalletListItemProps) => (props.data.account.IsLocked ?
                theme?.app?.applicationColors.tableRowWarningColor || 'rgba(245,130,74,0.16)'
                : theme?.app?.applicationColors.enumColors.white || '#fff'),
            cursor: (props: IWalletListItemProps) => (!props.data.account.IsLocked ? 'pointer' : 'inherit'),
            '&:hover': {
                backgroundColor: (props: IWalletListItemProps) => (!props.data.account.IsLocked ?
                    theme?.app?.applicationColors.enumColors.offWhite || '#F3F3F3'
                    : theme?.app?.applicationColors.tableRowWarningColor || 'rgba(245,130,74,0.16)')
            }
        },
        name: {
            fontSize: '1.08rem',
            lineHeight: 'normal',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            paddingTop: '.5rem'
        },
        secondName: {
            marginTop: '-.25rem',
            paddingTop: '.15em'
        },
        account: {
            fontSize: '1.08rem',
            lineHeight: '1.2rem',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        },
        activity: {
            fontSize: '1.5rem',
            lineHeight: '2rem',
            paddingTop: '.3rem',
            paddingBottom: '.5rem'
        },
        statusContainer: {
            height: '100%',
            paddingRight: '10px'
        },
        iconsContainer: { marginTop: '1rem' },
        statusIcon: {
            marginLeft: '.75rem',
            '&:first-child': { marginLeft: 0 },
            textWrap: 'nowrap'
        },
        primarySymbol: {
            marginLeft: '.5rem',
            '&:first-child': { marginLeft: 0 },
            color: '#202020',
            background: '#EDCB26',
            border: '1px solid #CFCFCF'
        }
    };
});

export function WalletInfoCellRenderer(props: IWalletListItemProps) {
    const { data } = props;

    const {
        account,
        userAccount,
        linkedBDAccountId
    } = data;

    const {
        root,
        name,
        secondName,
        activity,
        statusContainer,
        iconsContainer,
        statusIcon,
        primarySymbol
    } = muiStyles(props);

    const theme = useTheme<ApplicationMuiTheme>();

    const role = ClientWalletRolesMap.find(role => userAccount.ClientRoleId === role.id);

    return (
        <Grid
            alignItems='center'
            className={`${root} firm-info-cell`}
            component={!account.IsLocked ? Link : Grid}
            container
            direction='row'
            id={`${account.AccountName}_walletInfo`}
            spacing={0}
            title={`${account.AccountName}`}
            to={!account.IsLocked ? `/accounts/${account.AccountId}` : ''}
        >
            <Grid
                container
                direction='column'
                item
                spacing={1}
                wrap='nowrap'
                xs={7}
            >
                <Box
                    className={name}
                    fontWeight='bold'
                    id={`${account.AccountName}_accountName`}
                >{account.AccountName}
                </Box>
                <Grid
                    alignItems='center'
                    className={iconsContainer}
                    container
                    direction='row'
                    justifyContent='flex-start'
                    spacing={0}
                    wrap='nowrap'
                >
                    {account.IsLocked &&
                        <LockIcon
                            className={statusIcon}
                            iconColor={theme?.app?.applicationColors.lockColor || LOCK_ICON_COLOR}
                            id={`${account.AccountName}_lockedStatus`}
                        />}
                    {account.IsPrimary &&
                        <AttributeBadge
                            active
                            className={primarySymbol}
                            value='P'
                        />}
                    {account.IsLiquidityFacility &&
                        <AttributeTag
                            attribute='LF'
                            backgroundColor='#FDF9E2'
                            borderColor='#EDCB26'
                            className={statusIcon}
                            id={`${account.AccountName}_LiquidityFacilityStatus`}
                        />}
                    {userAccount.IsFavorite &&
                        <FavoriteIcon
                            className={statusIcon}
                            iconColor={theme?.app?.clientTheme.colors.highlightColor1 || '#EDDA26'}
                            id={`${account.AccountName}_favoriteStatus`}
                        />}
                </Grid>
            </Grid>
            <Grid
                alignItems='flex-end'
                className={statusContainer}
                container
                direction='column'
                item
                justifyContent='center'
                spacing={0}
                wrap='nowrap'
                xs={5}
            >
                <Box
                    className={name}
                    id={`${(account as any).linkedBrokerDealerAccount}_linkedBDAccountId`}
                >{obscureAccountNo((account as any).linkedBrokerDealerAccount)}
                </Box>
                <Grid
                    alignItems='flex-end'
                    className={iconsContainer}
                    container
                    direction='row'
                    justifyContent='flex-end'
                    spacing={0}
                    wrap='nowrap'
                >
                    <ClientRoleSymbol
                        className={statusIcon}
                        id={`${account.AccountName}_clientRole`}
                        role={role.displayName}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default (WalletInfoCellRenderer);